import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { useBreakpoint } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import { useIntroStore } from "@/store/introStore";
import VariableImage from "./ui/VariableImage";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const HeroSection = ({
  video,
  title,
  text,
  image,
  SubTitle1,
  SubTitle2,
  dynamicComponent = false,
}: {
  video?: string;
  title: string;
  text: string;
  image?: {
    url: string;
    alternativeText: string;
    caption: string;
  };
  SubTitle1?: string;
  SubTitle2?: string;
  dynamicComponent?: boolean;
}) => {
  const introDone = useIntroStore((state) => state.introDone);
  const videoRef = useRef<HTMLVideoElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [isplaying, setIsplaying] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [muted, setMuted] = useState(true);
  const { isDesktop } = useBreakpoint();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;

    // iOS (Low Power Mode, data saver, autoplay settings) can refuse play()
    // outside a user gesture -- recover on the first touch; if even that
    // fails, flip the button state to match reality.
    const retryOnGesture = () => {
      video.play().catch(() => setIsplaying(false));
    };
    const playVideo = async () => {
      if (isplaying) {
        try {
          await video.play();
        } catch (error) {
          if ((error as DOMException).name === "NotAllowedError") {
            window.addEventListener("touchend", retryOnGesture, { once: true });
            window.addEventListener("pointerup", retryOnGesture, { once: true });
          }
        }
      } else {
        video.pause();
      }
    };
    playVideo();
    return () => {
      window.removeEventListener("touchend", retryOnGesture);
      window.removeEventListener("pointerup", retryOnGesture);
      video.pause();
    };
  }, [isplaying, muted]);

  useGSAP(
    () => {
      const video = videoRef.current;
      if (!video) return;
      ScrollTrigger.create({
        trigger: container.current,
        start: "bottom center",
        end: "top center",
        onEnter: () => {
          setIsplaying(false);
        },
        onEnterBack: () => {
          setIsplaying(true);
        },
      });
    },
    { scope: container },
  );

  useGSAP(
    () => {
      if (loaded && introDone) {
        gsap.timeline({ defaults: { ease: "power2.inOut", duration: 2 } }).from(
          "#heroSection .hero-element",
          {
            y: 50,
            autoAlpha: 0,
            stagger: 0.1,
          },
          "<30%",
        );
        ScrollTrigger.refresh();
      }
    },
    { dependencies: [loaded, isDesktop, introDone], revertOnUpdate: true },
  );

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section
      ref={container}
      id="heroSection"
      className="relative h-screen w-full overflow-hidden"
    >
      {video && (
        <video
          ref={videoRef}
          src={video}
          id="heroImg"
          className="absolute inset-0 size-full object-cover will-change-auto"
          autoPlay
          loop
          playsInline
          poster={image?.url}
          preload="metadata"
        />
      )}
      {image && !video && (
        <VariableImage
          src={image.url}
          className="absolute inset-0 size-full object-cover will-change-auto"
          fill={true}
          sizes="100vw"
          alt={image.alternativeText}
          caption={image.caption}
        />
      )}

      <div className="container relative z-2 h-full">
        <div className="flex h-full flex-col justify-center pt-[clamp(20px,30vw,170px)] pb-[clamp(20px,5vw,50px)] text-white lg:justify-between">
          <h1 className="hero-element title-50 lg:max-w-[600px]">{title}</h1>
          <div
            className={cn(
              "flex flex-col",
              dynamicComponent && "items-end rtl:items-start",
            )}
          >
            <div>
              {(SubTitle1 || SubTitle2) && (
                <div className="mb-5 space-y-2">
                  {SubTitle1 && (
                    <h2 className="hero-element font-semibold lg:text-2xl">
                      {SubTitle1}
                    </h2>
                  )}
                  {SubTitle2 && (
                    <h3 className="hero-element font-semibold">{SubTitle2}</h3>
                  )}
                </div>
              )}

              <div className="flex-between flex-wrap gap-10">
                <div className="hero-element">
                  <span className="seperator bg-white"></span>
                  <p className="whitespace-pre-line pt-5 lg:max-w-[600px]">
                    {text}
                  </p>
                </div>
                {video && (
                  <div className="hero-element w-fit flex-center gap-5 rtl:flex-row-reverse">
                    <button
                      onClick={() => setIsplaying(!isplaying)}
                      className="relative flex size-14 items-center justify-center rounded-full border border-white"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={cn(
                          "absolute size-5 opacity-0 transition-opacity duration-300",
                          !isplaying && "opacity-100",
                        )}
                        viewBox="0 0 512 512"
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeMiterlimit="10"
                          strokeWidth="32"
                          d="M112 111v290c0 17.44 17 28.52 31 20.16l247.9-148.37c12.12-7.25 12.12-26.33 0-33.58L143 90.84c-14-8.36-31 2.72-31 20.16Z"
                        />
                      </svg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={cn(
                          "size-5 transition-opacity duration-300",
                          !isplaying && "opacity-0",
                        )}
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M9 5a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1m6 0a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => setMuted(!muted)}
                      className="relative flex size-14 items-center justify-center rounded-full border border-white"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={cn(
                          "absolute size-5 opacity-0 transition-opacity duration-300",
                          muted && "opacity-100",
                        )}
                      >
                        <g fill="none">
                          <path
                            fill="currentColor"
                            d="M16.25 12a.75.75 0 0 0-1.5 0zM7.016 6.958l.125.74zM8.6 6.22l-.413-.626zm-.871.524l.303.687zM3.34 16.723l-.315.68zm-1.805-3.695l-.749.046zm.424 2.395l.66-.356zM13.7 20.075l-.242-.71zm1.713-5.47l.749.035zM14.28 19.78l.43.613zM8.818 6.076l.413.626zM13.7 3.925l-.242.71zm.58.295l.43-.614zM3.34 7.277l-.315-.68zm-1.805 3.694l-.749-.045zm.424-2.394l.66.356zm7.952 9.168A.75.75 0 0 0 9.09 19zM9.012 6.846l.22-.144l-.827-1.252l-.219.144zm-6.729 6.137c-.02-.347-.033-.68-.033-.983H.75c0 .341.014.706.036 1.074zM2.25 12c0-.303.012-.636.033-.983l-1.497-.091A18 18 0 0 0 .75 12zm12.5 0c0 .512-.034 1.45-.086 2.57l1.498.07A68 68 0 0 0 16.25 12zM6 7.75c.488 0 .817.002 1.141-.053l-.25-1.479c-.171.03-.354.032-.89.032zm2.186-2.156c-.447.295-.602.394-.76.464l.605 1.373c.3-.133.574-.316.981-.585zM7.141 7.697a3.8 3.8 0 0 0 .89-.266l-.606-1.373q-.256.114-.534.16zM6.001 17.75c.536 0 .719.002.89.031l.25-1.479c-.324-.055-.653-.052-1.14-.052zm0-1.5c-1.445 0-1.932-.017-2.346-.208l-.63 1.361c.784.363 1.67.347 2.975.347zM.785 13.074c.07 1.153.104 1.947.512 2.704l1.32-.711c-.217-.405-.258-.82-.335-2.084zm2.869 2.968a2.47 2.47 0 0 1-1.036-.975l-1.32.711a3.96 3.96 0 0 0 1.726 1.625zm11.009-1.472c-.072 1.531-.123 2.603-.262 3.378c-.14.771-.337 1.066-.553 1.218l.862 1.227c.706-.496 1.005-1.28 1.167-2.18c.161-.895.215-2.086.284-3.573zm-.722 6.215a3 3 0 0 0 .769-.392l-.863-1.227a1.6 1.6 0 0 1-.39.199zM9.232 6.702c1.289-.85 2.194-1.445 2.908-1.792c.712-.346 1.069-.36 1.318-.275l.484-1.42c-.818-.28-1.631-.056-2.457.345c-.824.4-1.826 1.063-3.08 1.89zm4.226-2.067c.137.047.272.115.39.198l.863-1.227a3 3 0 0 0-.769-.39zM6 6.25c-1.304 0-2.19-.016-2.975.346l.63 1.362c.414-.192.901-.208 2.345-.208zm-3.717 4.767c.077-1.264.118-1.68.336-2.084l-1.32-.712C.89 8.979.855 9.772.785 10.926zm.742-4.42A3.96 3.96 0 0 0 1.298 8.22l1.32.712a2.47 2.47 0 0 1 1.037-.975zM9.089 19c1.039.68 1.899 1.224 2.631 1.549c.743.328 1.48.489 2.222.236l-.484-1.42c-.226.077-.54.074-1.13-.188c-.602-.266-1.358-.738-2.417-1.432zm7.034-10.46c-.06-1.227-.127-2.233-.296-3.01c-.172-.789-.477-1.474-1.116-1.924l-.863 1.227c.196.138.377.392.513 1.016c.139.635.202 1.514.264 2.765z"
                          />
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeWidth="1.5"
                            d="M20 18s1.5-1.8 1.5-6c0-2.433-.503-4.061-.927-5M18 15s.5-.9.5-3c0-.862-.084-1.522-.183-2M22 2L2 22"
                          />
                        </g>
                      </svg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className={cn(
                          "size-5 transition-opacity duration-300",
                          !muted && "opacity-100",
                        )}
                      >
                        <g fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M1.535 10.971c.073-1.208.11-1.813.424-2.394a3.2 3.2 0 0 1 1.38-1.3C3.94 7 4.627 7 6 7c.512 0 .768 0 1.016-.042a3 3 0 0 0 .712-.214c.23-.101.444-.242.871-.524l.22-.144C11.36 4.399 12.632 3.56 13.7 3.925c.205.07.403.17.58.295c.922.648.993 2.157 1.133 5.174A68 68 0 0 1 15.5 12c0 .532-.035 1.488-.087 2.605c-.14 3.018-.21 4.526-1.133 5.175a2.3 2.3 0 0 1-.58.295c-1.067.364-2.339-.474-4.882-2.151L8.6 17.78c-.427-.282-.64-.423-.871-.525a3 3 0 0 0-.712-.213C6.768 17 6.512 17 6 17c-1.374 0-2.06 0-2.66-.277a3.2 3.2 0 0 1-1.381-1.3c-.314-.582-.35-1.186-.424-2.395A17 17 0 0 1 1.5 12c0-.323.013-.671.035-1.029Z" />
                          <path
                            strokeLinecap="round"
                            d="M20 6s1.5 1.8 1.5 6s-1.5 6-1.5 6m-2-9s.5.9.5 3s-.5 3-.5 3"
                          />
                        </g>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
